/**
 * NoProjectsPage — страница для проектировщика без активных проектов.
 * Показывает мини-игру 2048 с аркадной таблицей рекордов.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gameApi } from '@/api/game'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'

// ─────────────────────────────────────────────────────────────
// Константы и типы
// ─────────────────────────────────────────────────────────────

const SIZE = 4

type Board = number[][]
type Direction = 'up' | 'down' | 'left' | 'right'

interface GameState {
  board: Board
  score: number
  best: number
  status: 'playing' | 'won' | 'over'
}

// ─────────────────────────────────────────────────────────────
// Чистая логика игры 2048
// ─────────────────────────────────────────────────────────────

function emptyBoard(): Board {
  return Array.from({ length: SIZE }, () => Array(SIZE).fill(0))
}

function addRandom(board: Board): Board {
  const empty: [number, number][] = []
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++)
      if (board[r][c] === 0) empty.push([r, c])
  if (empty.length === 0) return board

  const [r, c] = empty[Math.floor(Math.random() * empty.length)]
  const next = board.map((row) => [...row])
  next[r][c] = Math.random() < 0.9 ? 2 : 4
  return next
}

function initBoard(): Board {
  return addRandom(addRandom(emptyBoard()))
}

/** Сдвигает одну строку влево, возвращает { row, gained }. */
function slideRow(row: number[]): { row: number[]; gained: number } {
  const nums = row.filter((v) => v !== 0)
  let gained = 0
  const merged: number[] = []
  let i = 0
  while (i < nums.length) {
    if (i + 1 < nums.length && nums[i] === nums[i + 1]) {
      const val = nums[i] * 2
      merged.push(val)
      gained += val
      i += 2
    } else {
      merged.push(nums[i])
      i++
    }
  }
  while (merged.length < SIZE) merged.push(0)
  return { row: merged, gained }
}

function rotate90(board: Board): Board {
  return Array.from({ length: SIZE }, (_, r) =>
    Array.from({ length: SIZE }, (_, c) => board[SIZE - 1 - c][r])
  )
}

function moveBoard(
  board: Board,
  dir: Direction
): { board: Board; gained: number; moved: boolean } {
  // Все направления сводим к «сдвиг влево» через повороты
  let rotations = 0
  if (dir === 'up') rotations = 1
  else if (dir === 'right') rotations = 2
  else if (dir === 'down') rotations = 3

  let b = board
  for (let i = 0; i < rotations; i++) b = rotate90(b)

  let totalGained = 0
  const newRows = b.map((row) => {
    const { row: newRow, gained } = slideRow(row)
    totalGained += gained
    return newRow
  })

  let moved =
    totalGained > 0 ||
    newRows.some((row, r) => row.some((v, c) => v !== b[r][c]))

  // Обратный поворот
  let result = newRows
  for (let i = 0; i < (4 - rotations) % 4; i++) result = rotate90(result)

  return { board: result, gained: totalGained, moved }
}

function hasWon(board: Board): boolean {
  return board.some((row) => row.some((v) => v === 2048))
}

function hasMoves(board: Board): boolean {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      if (board[r][c] === 0) return true
      if (c + 1 < SIZE && board[r][c] === board[r][c + 1]) return true
      if (r + 1 < SIZE && board[r][c] === board[r + 1][c]) return true
    }
  return false
}

// ─────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────

type Action =
  | { type: 'MOVE'; dir: Direction }
  | { type: 'RESTART' }
  | { type: 'CONTINUE' }  // продолжаем после 2048

function gameReducer(state: GameState, action: Action): GameState {
  if (action.type === 'RESTART') {
    return { board: initBoard(), score: 0, best: state.best, status: 'playing' }
  }
  if (action.type === 'CONTINUE') {
    return { ...state, status: 'playing' }
  }
  if (action.type === 'MOVE') {
    if (state.status === 'over') return state

    const { board, gained, moved } = moveBoard(state.board, action.dir)
    if (!moved) return state

    const newBoard = addRandom(board)
    const newScore = state.score + gained
    const newBest = Math.max(state.best, newScore)

    if (state.status !== 'won' && hasWon(newBoard)) {
      return { board: newBoard, score: newScore, best: newBest, status: 'won' }
    }
    if (!hasMoves(newBoard)) {
      return { board: newBoard, score: newScore, best: newBest, status: 'over' }
    }
    return { board: newBoard, score: newScore, best: newBest, status: 'playing' }
  }
  return state
}

function initState(): GameState {
  return { board: initBoard(), score: 0, best: 0, status: 'playing' }
}

// ─────────────────────────────────────────────────────────────
// Цвета плиток
// ─────────────────────────────────────────────────────────────

const TILE_STYLES: Record<number, string> = {
  0:    'bg-gray-100 text-gray-100',
  2:    'bg-amber-100 text-gray-700',
  4:    'bg-amber-200 text-gray-700',
  8:    'bg-orange-300 text-white',
  16:   'bg-orange-400 text-white',
  32:   'bg-orange-500 text-white',
  64:   'bg-red-500 text-white',
  128:  'bg-yellow-400 text-white',
  256:  'bg-yellow-500 text-white',
  512:  'bg-yellow-600 text-white',
  1024: 'bg-lime-500 text-white',
  2048: 'bg-green-500 text-white font-black',
}

function tileStyle(val: number): string {
  return TILE_STYLES[val] ?? 'bg-purple-600 text-white font-black'
}

function tileFontSize(val: number): string {
  if (val >= 1000) return 'text-sm'
  if (val >= 100) return 'text-base'
  return 'text-lg'
}

// ─────────────────────────────────────────────────────────────
// Компонент игры
// ─────────────────────────────────────────────────────────────

interface GameProps {
  onGameOver: (score: number) => void
}

function Game2048({ onGameOver }: GameProps) {
  const [state, dispatch] = useReducer(gameReducer, undefined, initState)
  const boardRef = useRef<HTMLDivElement>(null)
  const gameOverReported = useRef(false)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  // Управление клавиатурой
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const map: Record<string, Direction> = {
        ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        w: 'up', s: 'down', a: 'left', d: 'right',
        W: 'up', S: 'down', A: 'left', D: 'right',
      }
      const dir = map[e.key]
      if (dir) {
        e.preventDefault()
        dispatch({ type: 'MOVE', dir })
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Репорт о проигрыше
  useEffect(() => {
    if (state.status === 'over' && !gameOverReported.current) {
      gameOverReported.current = true
      onGameOver(state.score)
    }
  }, [state.status, state.score, onGameOver])

  // Свайп на мобильном
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y
    const absDx = Math.abs(dx), absDy = Math.abs(dy)
    if (Math.max(absDx, absDy) < 20) return
    if (absDx > absDy) dispatch({ type: 'MOVE', dir: dx > 0 ? 'right' : 'left' })
    else dispatch({ type: 'MOVE', dir: dy > 0 ? 'down' : 'up' })
    touchStartRef.current = null
  }

  const restart = () => {
    gameOverReported.current = false
    dispatch({ type: 'RESTART' })
  }

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Шапка: очки */}
      <div className="flex items-center gap-4 w-full max-w-xs justify-between">
        <div className="flex gap-3">
          <ScoreBox label="Счёт" value={state.score} />
          <ScoreBox label="Рекорд сессии" value={state.best} />
        </div>
        <button
          onClick={restart}
          className="rounded-lg bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-300 transition-colors"
        >
          Новая игра
        </button>
      </div>

      {/* Доска */}
      <div
        ref={boardRef}
        className="relative select-none bg-gray-300 rounded-xl p-2 grid gap-2 touch-none"
        style={{ gridTemplateColumns: `repeat(${SIZE}, 1fr)`, width: 280, height: 280 }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {state.board.flat().map((val, i) => (
          <div
            key={i}
            className={`flex items-center justify-center rounded-lg font-bold transition-colors ${tileStyle(val)} ${tileFontSize(val)}`}
          >
            {val !== 0 ? val : ''}
          </div>
        ))}

        {/* Оверлей — победа */}
        {state.status === 'won' && (
          <div className="absolute inset-0 rounded-xl flex flex-col items-center justify-center gap-3 bg-green-500/90 text-white">
            <div className="text-3xl font-black">🎉 2048!</div>
            <p className="text-sm">Отличный результат!</p>
            <button
              onClick={() => dispatch({ type: 'CONTINUE' })}
              className="rounded-lg bg-white text-green-600 px-4 py-1.5 text-sm font-semibold hover:bg-green-50"
            >
              Продолжить
            </button>
            <button
              onClick={restart}
              className="text-xs text-white/80 underline"
            >
              Новая игра
            </button>
          </div>
        )}

        {/* Оверлей — проигрыш */}
        {state.status === 'over' && (
          <div className="absolute inset-0 rounded-xl flex flex-col items-center justify-center gap-3 bg-gray-800/80 text-white">
            <div className="text-2xl font-black">Игра окончена</div>
            <p className="text-sm">Счёт: {state.score}</p>
            <button
              onClick={restart}
              className="rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-semibold hover:bg-brand-700"
            >
              Сыграть ещё
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400">Стрелки / WASD / свайп для управления</p>
    </div>
  )
}

function ScoreBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-gray-700 text-white px-3 py-1.5 text-center min-w-[72px]">
      <div className="text-[10px] uppercase tracking-wide text-gray-300">{label}</div>
      <div className="font-bold text-base leading-tight">{value}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Диалог сохранения рекорда
// ─────────────────────────────────────────────────────────────

interface SaveScoreDialogProps {
  score: number
  onSave: (nickname: string) => void
  onSkip: () => void
  isSaving: boolean
}

function SaveScoreDialog({ score, onSave, onSkip, isSaving }: SaveScoreDialogProps) {
  const [nick, setNick] = useState('')

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-center space-y-3 w-full max-w-xs">
      <div className="text-2xl">🏆</div>
      <p className="font-semibold text-gray-900">Ваш счёт: {score}</p>
      <p className="text-sm text-gray-500">Хотите войти в таблицу рекордов?</p>
      <input
        type="text"
        maxLength={10}
        value={nick}
        onChange={(e) => setNick(e.target.value)}
        placeholder="Псевдоним (до 10 символов)"
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-center focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSave(nick)}
          disabled={isSaving}
          className="flex-1 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {isSaving ? 'Сохраняем...' : 'Сохранить'}
        </button>
        <button
          onClick={onSkip}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          Пропустить
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Таблица рекордов
// ─────────────────────────────────────────────────────────────

function Leaderboard() {
  const { data: scores = [], isLoading } = useQuery({
    queryKey: ['game-leaderboard'],
    queryFn: () => gameApi.leaderboard().then((r) => r.data),
    refetchInterval: 30_000,
  })

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden w-full max-w-xs">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <span className="text-base">🏅</span>
        <span className="font-semibold text-sm text-gray-900">Таблица рекордов</span>
      </div>
      {isLoading ? (
        <div className="px-4 py-6 text-center text-sm text-gray-400">Загрузка...</div>
      ) : scores.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          Рекордов пока нет - будь первым!
        </div>
      ) : (
        <ol className="divide-y divide-gray-50">
          {scores.map((s, i) => (
            <li key={s.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className={`w-5 text-center text-sm font-bold ${
                i === 0 ? 'text-yellow-500' :
                i === 1 ? 'text-gray-400' :
                i === 2 ? 'text-orange-400' : 'text-gray-300'
              }`}>
                {i + 1}
              </span>
              <span className="flex-1 text-sm text-gray-700 truncate">
                {s.nickname ?? <span className="text-gray-400 italic">Аноним</span>}
              </span>
              <span className="font-bold text-sm text-gray-900">{s.score.toLocaleString()}</span>
              <span className="text-xs text-gray-400 hidden sm:block">
                {format(new Date(s.achieved_at), 'd MMM', { locale: ru })}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Главная страница
// ─────────────────────────────────────────────────────────────

export default function NoProjectsPage() {
  const qc = useQueryClient()
  const [pendingScore, setPendingScore] = useState<number | null>(null)
  const [scoreSaved, setScoreSaved] = useState(false)
  const [gameKey, setGameKey] = useState(0)

  const submitMutation = useMutation({
    mutationFn: ({ score, nickname }: { score: number; nickname: string }) =>
      gameApi.submitScore(score, nickname || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-leaderboard'] })
      setPendingScore(null)
      setScoreSaved(true)
      setGameKey((k) => k + 1)
    },
  })

  const handleGameOver = useCallback((score: number) => {
    if (score > 0) setPendingScore(score)
  }, [])

  const handleSkip = () => {
    setPendingScore(null)
    setGameKey((k) => k + 1)
  }

  return (
    <div className="p-6 flex flex-col items-center gap-6">
      {/* Заголовок */}
      <div className="text-center">
        <h1 className="text-xl font-semibold text-gray-900">Нет активных проектов</h1>
        <p className="mt-1 text-sm text-gray-500">
          Как только ГИП назначит вас на проект - он появится здесь. А пока…
        </p>
      </div>

      {scoreSaved && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-700">
          Результат сохранён! 🎉
        </div>
      )}

      <div className="flex flex-col lg:flex-row items-start gap-8 w-full max-w-2xl justify-center">
        {/* Игра или диалог сохранения */}
        <div className="flex flex-col items-center gap-4">
          {pendingScore !== null ? (
            <SaveScoreDialog
              score={pendingScore}
              onSave={(nick) => submitMutation.mutate({ score: pendingScore, nickname: nick })}
              onSkip={handleSkip}
              isSaving={submitMutation.isPending}
            />
          ) : (
            <Game2048 key={gameKey} onGameOver={handleGameOver} />
          )}
        </div>

        {/* Таблица рекордов */}
        <Leaderboard />
      </div>
    </div>
  )
}
